import {
  hasBytesLanded,
  type MediaUploadJob,
} from "@/features/shared/media-upload";
import type { FormAttachment } from "@/libs/form-attachment";

/** What only the browser knows about one picked file. */
export interface UploadPick {
  uid: string;
  /** The name/size/type as picked — the row's identity until the job reports. */
  fileName: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
  /** Object URL (or a poster drawn off the video) when the browser can paint the bytes. */
  previewUrl?: string;
}

export interface JobToFormAttachmentOptions {
  /**
   * The consumer stores the URL itself (a note's image, the signed-out vendor
   * forms), not the attachment id — so a file the media service will REWRITE to a
   * new key (HEIC → webp, audio → mp3, non-compliant video → mp4) must not report
   * `done` until processing announced that final URL. Id-based forms leave this
   * off: for them the bytes landing is the whole gate.
   */
  awaitFinalUrl?: boolean;
}

/** The form's send gate — the row turns `done` once the bytes have landed. */
export { hasBytesLanded };

/**
 * Whether the media service may serve this file from a NEW key after processing,
 * retiring the URL `/init` handed out. Every PHOTO is rewritten to webp (verified
 * on dev 2026-09-02: a JPEG/PNG original lands as `<id>/<name>.webp`), every audio
 * is re-encoded to mp3, and a video is transcoded when it is not already a
 * compliant H.264 MP4 — unknowable here, so every video counts. Only `FILE` kinds
 * (PDF, office, text, archives) are stored as-is.
 */
export function mayBeRewritten(pick: {
  fileName: string;
  contentType: string;
}): boolean {
  const type = pick.contentType.toLowerCase();
  if (
    type.startsWith("image/") ||
    type.startsWith("audio/") ||
    type.startsWith("video/")
  ) {
    return true;
  }
  const ext = pick.fileName.split(".").pop()?.toLowerCase() ?? "";
  return REWRITABLE_EXTENSIONS.has(ext);
}

/** Extension fallback for a File whose `type` the browser left empty. */
const REWRITABLE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "tiff",
  "tif",
  "heic",
  "heif",
  "hif",
  "mp4",
  "mov",
  "avi",
  "mkv",
  "webm",
  "wmv",
  "hevc",
  "mp3",
  "wav",
  "m4a",
  "aac",
  "ogg",
  "opus",
  "weba",
  "flac",
]);

/**
 * Progress for an in-flight row. The bytes the browser has pushed own 0–90 %
 * (the part count is the fallback before the first byte counter arrives);
 * finalizing sits at 95 so the ring keeps creeping through the last round trip
 * instead of freezing.
 */
function uploadingPercent(job: MediaUploadJob): number {
  if (job.phase === "finalizing") return 95;
  const byBytes = job.size > 0 ? (job.bytesSent / job.size) * 90 : 0;
  const byParts = job.parts > 0 ? (job.partsDone / job.parts) * 90 : 0;
  return Math.min(90, Math.round(Math.max(byBytes, byParts)));
}

/**
 * Project one media-v3 upload job into the `FormAttachment` row a form field
 * holds. Pure — pinned by `__tests__/job-to-form-attachment.test.ts`.
 *
 * `id` is the media attachmentId as soon as `/init` minted it (a row can be
 * removed or deduped by a real id before its bytes finish), and the local uid
 * until then — the renderers quote `id` back on remove/retry, and a row whose
 * `/init` never answered has no other key. A stored id only ever reaches an API
 * from a `done` row, which always carries the attachmentId.
 * `thumbnailUri` is the CDN rendition once the media service announced it, else
 * the local preview while it is still being made (a HEIC has no local preview —
 * its chip shows the type icon until the webp exists), else empty so renderers
 * fall back to the original object.
 */
export function jobToFormAttachment(
  job: MediaUploadJob,
  pick: UploadPick,
  options: JobToFormAttachmentOptions = {},
): FormAttachment {
  const base = {
    id: job.attachmentId ?? pick.uid,
    uid: pick.uid,
    fileName: pick.fileName,
    fileSize: job.size,
    contentType: pick.contentType || undefined,
    createdAt: pick.createdAt,
    thumbnailUri: pick.previewUrl ?? "",
  };

  if (job.phase === "failed") {
    return { ...base, status: "error", percent: 0 };
  }

  if (hasBytesLanded(job)) {
    const awaitingFinalUrl =
      options.awaitFinalUrl === true &&
      job.phase === "processing" &&
      mayBeRewritten(pick);
    if (awaitingFinalUrl) {
      return { ...base, status: "uploading", percent: 95 };
    }
    const contentUri = job.contentUri ?? undefined;
    return {
      ...base,
      status: "done",
      percent: 100,
      originalContentUri: contentUri,
      contentUri,
      thumbnailUri: job.thumbnailUri ?? pick.previewUrl ?? "",
    };
  }

  if (job.phase === "queued" || job.phase === "init") {
    return { ...base, status: "added", percent: 0 };
  }

  return { ...base, status: "uploading", percent: uploadingPercent(job) };
}
